/* CW Light Calculator v2 — vanilla JS, no dependencies.
   Data contract: Lyra's custom.calculator_data schema v1 (Fact Library canonical).
   Section data-calc-config (metafield JSON) overrides the hardcoded v1 fallback
   below. Looks, strand presets, spacing ranges, notes and CTA links are ALL
   data-driven — content side retunes numbers without a code deploy. */
(function () {
  'use strict';

  var DEFAULT_CONFIG = {
    tree: {
      looks: [
        { id: 'sparse', label: 'Sparse & sculptural', bulbs_per_foot: 75 },
        { id: 'classic', label: 'Classic medium', bulbs_per_foot: 100 },
        { id: 'rich', label: 'Rich & editorial', bulbs_per_foot: 150 },
        { id: 'sparkle', label: 'Maximum sparkle', bulbs_per_foot: 200 }
      ],
      heights_ft: [4, 5, 6, 7, 8, 9, 12],
      cluster_factor: 0.5,
      strands: [
        { id: 'fairy100', label: '10m fairy strand (100 bulbs)', bulbs: 100 },
        { id: 'reel1000', label: '1000-LED reel with timer', bulbs: 1000 },
        { id: 'cluster256', label: '256-LED cluster strand (3m)', bulbs: 256 },
        { id: 'cluster384', label: '384-LED cluster strand', bulbs: 384 },
        { id: 'cluster2000', label: '2000-LED cluster strand (29m)', bulbs: 2000 }
      ],
      default_strand_bulbs: 100
    },
    roofline: { swag_pct_min: 10, swag_pct_max: 15 },
    wrap: { spacing_cm_min: 5, spacing_cm_max: 10, default_spacing_cm: 10 },
    bush: { net_per_bush: 1, net_sizes_m: [4.8, 9.6] },
    paths: { solar_stake_spacing_cm: [50, 80], rope_reel_per_m: [8, 10] },
    power_estimate: { kwh_price: 0.35, led_watts_per_100_bulbs: [2, 3.6] },
    cta: {
      primary: { label: 'Shop Christmas Lights', url: '/collections/christmas-lights' },
      type_links: [
        { label: 'Fairy lights', url: '/collections/led-fairy-lights' },
        { label: 'Cluster lights', url: '/collections/cluster-lights' },
        { label: 'Icicle lights', url: '/collections/led-icicle-lights' },
        { label: 'Net lights', url: '/collections/led-net-lights' },
        { label: 'Rope lights', url: '/collections/christmas-rope-lights' }
      ]
    }
  };

  var SEASON_HOURS = 270; /* estimate basis: 45 evenings x 6h */

  function deepMerge(base, over) {
    var out = {};
    for (var k in base) {
      out[k] = (base[k] && typeof base[k] === 'object' && !Array.isArray(base[k]))
        ? deepMerge(base[k], over && over[k] || {}) : base[k];
    }
    if (over) for (var k2 in over) {
      if (!(k2 in out) || !(base && k2 in base)) out[k2] = over[k2];
    }
    return out;
  }

  function loadCfg(raw) {
    if (!raw) return DEFAULT_CONFIG;
    try { return deepMerge(DEFAULT_CONFIG, JSON.parse(raw)); }
    catch (e) { return DEFAULT_CONFIG; }
  }

  function round10(n) { return Math.round(n / 10) * 10; }
  function r1(n) { return Math.round(n * 10) / 10; }
  function money(n) { return '$' + (Math.round(n * 100) / 100).toFixed(2); }

  function find(list, id, fallbackId) {
    if (!list || !list.length) return null;
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    for (var j = 0; j < list.length; j++) if (list[j].id === fallbackId) return list[j];
    return list[0];
  }

  function typeLink(cfg, match) {
    var links = (cfg.cta && cfg.cta.type_links) || [];
    for (var i = 0; i < links.length; i++) if (links[i].label === match) return links[i];
    return null;
  }

  function compute(s, cfg) {
    var looks = (cfg.tree && cfg.tree.looks) || [];
    var look = find(looks, s.look, 'classic') || { bulbs_per_foot: 100 };
    var preset = find((cfg.tree && cfg.tree.strands) || [], s.strandId, 'fairy100') || { bulbs: 100, label: '10m fairy strand (100 bulbs)' };

    if (s.mode === 'tree') {
      var h = Math.min(14, Math.max(2, parseFloat(s.height) || 7));
      var bulbs = h * (look.bulbs_per_foot || 100);
      if (s.treetype === 'cluster') bulbs *= ((cfg.tree && cfg.tree.cluster_factor) || 0.5);
      bulbs = round10(bulbs);
      var strands = Math.ceil(bulbs / (preset.bulbs || 100));
      var pe = cfg.power_estimate || {};
      var watts = (pe.led_watts_per_100_bulbs || [2, 3.6]);
      var cLo = (bulbs / 100) * watts[0] / 1000 * SEASON_HOURS * (pe.kwh_price || 0.35);
      var cHi = (bulbs / 100) * watts[1] / 1000 * SEASON_HOURS * (pe.kwh_price || 0.35);
      var link = s.treetype === 'cluster'
        ? (typeLink(cfg, 'Cluster lights') || (cfg.cta && cfg.cta.primary) || { url: '/collections/cluster-lights', label: 'Shop cluster lights' })
        : (typeLink(cfg, 'Fairy lights') || (cfg.cta && cfg.cta.primary) || { url: '/collections/led-fairy-lights', label: 'Shop fairy lights' });
      return {
        primary: '\u2248 ' + bulbs.toLocaleString('en-AU') + ' bulbs',
        secondary: '\u2248 ' + strands + ' \u00d7 ' + preset.label,
        power: 'Estimated to run: ' + money(cLo) + '\u2013' + money(cHi) +
          ' for the season (45 evenings \u00d7 6h at ~$' + (pe.kwh_price || 0.35) + '/kWh)',
        tip: 'Respect the connectable limit \u2014 usually 3\u20136 strands end-to-end, spread runs across outlets.',
        cta: link
      };
    }

    if (s.mode === 'linear') {
      var m = Math.max(1, parseFloat(s.length) || 20);
      var swMin = (cfg.roofline && cfg.roofline.swag_pct_min) || 10;
      var swMax = (cfg.roofline && cfg.roofline.swag_pct_max) || 15;
      var lo = r1(m), hi = r1(m);
      if (s.swag) { lo = r1(m * (1 + swMin / 100)); hi = r1(m * (1 + swMax / 100)); }
      var lstrands = Math.ceil(hi / 10);
      var ic = typeLink(cfg, 'Icicle lights') || (cfg.cta && cfg.cta.primary) || { url: '/collections/led-icicle-lights', label: 'Shop icicle lights' };
      return {
        primary: '\u2248 ' + (s.swag ? lo + '\u2013' + hi : lo) + 'm of lights',
        secondary: '\u2248 ' + lstrands + ' strand' + (lstrands > 1 ? 's' : '') + ' (10m each)',
        power: '',
        tip: s.swag
          ? 'Swag covers droop and corners. Icicle lights instead? Width-only \u2014 the drops do the visual work.'
          : 'No swag added \u2014 best for flush fence and railing runs.',
        cta: s.swag ? ((cfg.cta && cfg.cta.primary) || { url: '/collections/christmas-lights', label: 'Shop Christmas Lights' }) : ic
      };
    }

    if (s.mode === 'wrap') {
      var wh = Math.max(0.5, parseFloat(s.wrapHeight) || 2);
      var circ = Math.max(10, parseFloat(s.circ) || 35);
      var sp = Math.max(2, parseFloat(s.spacing) || ((cfg.wrap && cfg.wrap.default_spacing_cm) || 10));
      var wraps = Math.round((wh * 100) / sp);
      var len = r1(wraps * (circ / 100));
      var wstrands = Math.ceil(len / 10);
      return {
        primary: '\u2248 ' + len + 'm of strand (' + wraps + ' wraps)',
        secondary: '\u2248 ' + wstrands + ' strand' + (wstrands > 1 ? 's' : '') + ' (10m each)',
        power: '',
        tip: 'Wrapped trunks take rain outdoors \u2014 use IP44 minimum, and link only to the manufacturer\u2019s connectable limit.',
        cta: (cfg.cta && cfg.cta.primary) || { url: '/collections/christmas-lights', label: 'Shop Christmas Lights' }
      };
    }

    if (s.mode === 'bush') {
      var n = Math.max(1, Math.round(parseFloat(s.bushes) || 3));
      var sizes = (cfg.bush && cfg.bush.net_sizes_m) || [4.8, 9.6];
      var nets = n * ((cfg.bush && cfg.bush.net_per_bush) || 1);
      var nl = typeLink(cfg, 'Net lights') || { url: '/collections/led-net-lights', label: 'Shop net lights' };
      return {
        primary: '\u2248 ' + nets + ' net light' + (nets > 1 ? 's' : ''),
        secondary: 'one net per bush \u2014 ' + sizes.join('m or ') + 'm sizes',
        power: '',
        tip: 'Wrapping instead? Budget 3\u20134 wrapped strands per bush for the same coverage.',
        cta: nl
      };
    }

    if (s.mode === 'stakes') {
      var edge = Math.max(1, parseFloat(s.edge) || 10);
      var ssp = Math.max(10, parseFloat(s.stakeSpacing) || 65);
      var count = Math.ceil((edge * 100) / ssp);
      var rope = typeLink(cfg, 'Rope lights');
      return {
        primary: '\u2248 ' + count + ' solar stake' + (count > 1 ? 's' : ''),
        secondary: 'one per ' + ssp + 'cm of edge (' + edge + 'm run)',
        power: '',
        tip: rope
          ? 'Rope-light edging: about one reel per 8\u201310m of path \u2014 no maths needed.'
          : 'Solar stakes need no cord budgeting \u2014 position them where they\u2019ll catch daytime sun.',
        cta: { url: '/collections/solar-stakes', label: 'Shop solar stakes' }
      };
    }
    return null;
  }

  function init() {
    var root = document.querySelector('.cw-calc');
    if (!root || root.dataset.calcInit) return;
    root.dataset.calcInit = '1';

    var cfg = loadCfg(root.getAttribute('data-calc-config'));

    /* Relocate to a body_html mount placeholder when present (pillar-style pages) */
    var mount = document.getElementById('cw-calc-mount');
    var sectionEl = root.closest('.cw-calc-section') || root;
    if (mount && !mount.contains(sectionEl)) mount.appendChild(sectionEl);

    var state = { mode: 'tree', height: 7, look: 'classic', treetype: 'fairy',
      strandId: 'fairy100', length: 20, swag: true, wrapHeight: 2, circ: 35,
      spacing: (cfg.wrap && cfg.wrap.default_spacing_cm) || 10,
      bushes: 3, edge: 10, stakeSpacing: 65 };

    /* populate looks radios from config (content side owns the looks) */
    var looksWrap = root.querySelector('[data-calc-looks]');
    if (looksWrap) {
      looksWrap.innerHTML = '';
      ((cfg.tree && cfg.tree.looks) || []).forEach(function (lk, i) {
        var lab = document.createElement('label');
        lab.className = 'cw-calc__opt';
        lab.title = lk.note || '';
        var inp = document.createElement('input');
        inp.type = 'radio'; inp.name = 'cw-calc-look'; inp.value = lk.id;
        if (lk.id === 'classic') inp.checked = true;
        lab.appendChild(inp);
        lab.appendChild(document.createTextNode(' ' + lk.label));
        looksWrap.appendChild(lab);
      });
    }

    /* populate strand presets from config (matches live product titles) */
    var sel = root.querySelector('[data-calc-strand]');
    if (sel) {
      sel.innerHTML = '';
      ((cfg.tree && cfg.tree.strands) || []).forEach(function (st) {
        var o = document.createElement('option');
        o.value = st.id; o.textContent = st.label;
        if (st.id === 'fairy100') o.selected = true;
        sel.appendChild(o);
      });
    }

    /* populate height datalist from config */
    var dl = root.querySelector('#cw-calc-heights');
    if (dl) {
      dl.innerHTML = '';
      ((cfg.tree && cfg.tree.heights_ft) || []).forEach(function (ft) {
        var o = document.createElement('option');
        o.value = ft;
        dl.appendChild(o);
      });
    }

    var out = {
      primary: root.querySelector('[data-calc-out="primary"]'),
      secondary: root.querySelector('[data-calc-out="secondary"]'),
      power: root.querySelector('[data-calc-out="power"]'),
      tip: root.querySelector('[data-calc-out="tip"]'),
      cta: root.querySelector('[data-calc-out="cta"]')
    };

    function render() {
      var r = compute(state, cfg);
      if (!r) return;
      out.primary.textContent = r.primary;
      out.secondary.textContent = r.secondary;
      out.power.textContent = r.power || '';
      out.tip.textContent = r.tip;
      out.cta.setAttribute('href', (r.cta && r.cta.url) || '/collections/christmas-lights');
      var lbl = (r.cta && r.cta.label) || 'Shop Christmas Lights';
      out.cta.textContent = lbl.indexOf('Shop') === 0 ? lbl : 'Shop ' + lbl.charAt(0).toLowerCase() + lbl.slice(1);
    }

    function setMode(mode) {
      state.mode = mode;
      root.querySelectorAll('.cw-calc__mode').forEach(function (b) {
        var active = b.dataset.mode === mode;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      root.querySelectorAll('[data-panel]').forEach(function (p) {
        p.hidden = p.dataset.panel !== mode;
        p.classList.toggle('is-active', p.dataset.panel === mode);
      });
      render();
    }

    root.querySelectorAll('.cw-calc__mode').forEach(function (b) {
      b.addEventListener('click', function () { setMode(b.dataset.mode); });
    });

    root.querySelectorAll('[data-calc-input]').forEach(function (el) {
      var handler = function () {
        switch (el.id) {
          case 'cw-calc-height': state.height = el.value; break;
          case 'cw-calc-strand': state.strandId = el.value; break;
          case 'cw-calc-length': state.length = el.value; break;
          case 'cw-calc-swag': state.swag = el.checked; break;
          case 'cw-calc-wrap-height': state.wrapHeight = el.value; break;
          case 'cw-calc-circ': state.circ = el.value; break;
          case 'cw-calc-spacing':
            state.spacing = el.value;
            var sv = root.querySelector('[data-calc-spacing-val]');
            if (sv) sv.textContent = parseFloat(el.value) + ' cm';
            break;
          case 'cw-calc-bushes': state.bushes = el.value; break;
          case 'cw-calc-edge': state.edge = el.value; break;
          case 'cw-calc-stake-spacing':
            state.stakeSpacing = el.value;
            var sv2 = root.querySelector('[data-calc-stake-spacing-val]');
            if (sv2) sv2.textContent = parseFloat(el.value) + ' cm';
            break;
        }
        render();
      };
      el.addEventListener('input', handler);
      el.addEventListener('change', handler);
    });

    root.addEventListener('change', function (e) {
      if (e.target.name === 'cw-calc-look' && e.target.checked) { state.look = e.target.value; render(); }
      if (e.target.name === 'cw-calc-treetype' && e.target.checked) { state.treetype = e.target.value; render(); }
    });

    render();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
